import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, Loader, WifiOff } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryingIn, setRetryingIn] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const toast = useToast();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimerRef?.current) clearInterval(retryTimerRef?.current);
    };
  }, []);

  const from = location?.state?.from?.pathname || '/dashboard';

  const validateField = useCallback((name, value) => {
    if (name === 'email') {
      if (!value?.trim()) return 'Email address is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Please enter a valid email address';
      return '';
    }
    if (name === 'password') {
      if (!value) return 'Password is required';
      if (value?.length < 6) return 'Password must be at least 6 characters';
      return '';
    }
    return '';
  }, []);

  const mapError = useCallback((errMsg) => {
    const lowerErr = (errMsg || '')?.toLowerCase();
    if (
      lowerErr?.includes('failed to fetch') ||
      lowerErr?.includes('networkerror') ||
      lowerErr?.includes('network') ||
      lowerErr?.includes('fetch') ||
      lowerErr?.includes('connection') ||
      lowerErr?.includes('unavailable') ||
      lowerErr?.includes('authretryablefetcherror') ||
      lowerErr?.includes('unable to connect')
    ) {
      return { msg: 'Unable to connect to the server. Please check your internet connection and try again.', isNetwork: true };
    }
    if (
      lowerErr?.includes('invalid') ||
      lowerErr?.includes('credentials') ||
      lowerErr?.includes('wrong') ||
      lowerErr?.includes('password')
    ) {
      return { msg: 'Invalid email or password. Please check your credentials and try again.', isNetwork: false };
    }
    if (lowerErr?.includes('email not confirmed')) {
      return { msg: 'Please confirm your email address before signing in. Check your inbox for a confirmation link.', isNetwork: false };
    }
    if (lowerErr?.includes('too many')) {
      return { msg: 'Too many login attempts. Please wait a few minutes before trying again.', isNetwork: false };
    }
    return { msg: errMsg || 'Login failed. Please try again.', isNetwork: false };
  }, []);

  const startRetryCountdown = useCallback((seconds, onComplete) => {
    setRetryingIn(seconds);
    let remaining = seconds;
    if (retryTimerRef?.current) clearInterval(retryTimerRef?.current);
    retryTimerRef.current = setInterval(() => {
      remaining -= 1;
      if (mountedRef?.current) setRetryingIn(remaining);
      if (remaining <= 0) {
        clearInterval(retryTimerRef?.current);
        if (mountedRef?.current) {
          setRetryingIn(0);
          onComplete();
        }
      }
    }, 1000);
  }, []);

  const doLogin = useCallback(async (loginEmail, loginPassword, attempt = 0) => {
    if (!loginEmail?.trim() || !loginPassword) return;
    if (!mountedRef?.current) return;
    if (mountedRef?.current) {
      setIsLoading(true);
      setError('');
      setIsNetworkError(false);
    }
    try {
      const result = await signIn(loginEmail?.trim(), loginPassword);
      if (!mountedRef?.current) return;
      if (!result?.success) {
        const { msg, isNetwork } = mapError(result?.error);
        if (isNetwork && attempt < 2) {
          // Auto-retry with exponential backoff: 2s, 4s
          const delay = (attempt + 1) * 2;
          if (mountedRef?.current) {
            setRetryCount(attempt + 1);
            setIsNetworkError(true);
            setError(`Connection failed. Retrying in ${delay} seconds... (attempt ${attempt + 1}/2)`);
            setIsLoading(false);
          }
          startRetryCountdown(delay, () => {
            if (mountedRef?.current) doLogin(loginEmail, loginPassword, attempt + 1);
          });
          return;
        }
        if (mountedRef?.current) {
          setRetryCount(0);
          setIsNetworkError(isNetwork);
          setError(msg);
          toast?.error(msg, 'Sign In Failed');
        }
      } else {
        if (mountedRef?.current) {
          setRetryCount(0);
          setIsNetworkError(false);
        }
        toast?.success('Welcome back! Redirecting to your dashboard...', 'Signed In Successfully');
        navigate(from, { replace: true });
      }
    } catch (err) {
      if (!mountedRef?.current) return;
      const msg = 'An unexpected error occurred. Please try again.';
      setError(msg);
      toast?.error(msg, 'Error');
    } finally {
      if (mountedRef?.current) setIsLoading(false);
    }
  }, [signIn, navigate, from, startRetryCountdown, toast, mapError]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (retryingIn > 0) return;
    const emailErr = validateField('email', email);
    const passwordErr = validateField('password', password);
    setTouched({ email: true, password: true });
    setFieldErrors({ email: emailErr, password: passwordErr });
    if (emailErr || passwordErr) {
      toast?.warning('Please fix the form errors before submitting.', 'Validation Error');
      return;
    }
    await doLogin(email, password);
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!forgotEmail?.trim()) {
      setForgotError('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const result = await resetPassword(forgotEmail?.trim());
      if (result?.success) {
        setForgotSuccess(true);
        toast?.success(`Reset link sent to ${forgotEmail}`, 'Check Your Email');
      } else {
        setForgotError(result?.error || 'Failed to send reset email. Please try again.');
        toast?.error(result?.error || 'Failed to send reset email.', 'Error');
      }
    } catch (err) {
      setForgotError('Failed to send reset email. Please try again.');
      toast?.error('Failed to send reset email. Please try again.', 'Error');
    } finally {
      setForgotLoading(false);
    }
  };

  const fillDemoCredentials = (userEmail, userPassword) => {
    setEmail(userEmail);
    setPassword(userPassword);
    setError('');
    setIsNetworkError(false);
    setShowForgotPassword(false);
    setFieldErrors({ email: '', password: '' });
    setTouched({ email: false, password: false });
    toast?.info('Demo credentials filled. Click Sign In to continue.', 'Demo Mode');
  };

  const handleCancelRetry = () => {
    if (retryTimerRef?.current) clearInterval(retryTimerRef?.current);
    setRetryingIn(0);
    setRetryCount(0);
    setIsLoading(false);
    setError('');
    setIsNetworkError(false);
  };

  const handleEmailChange = (e) => {
    const val = e?.target?.value;
    setEmail(val);
    if (touched?.email) {
      setFieldErrors(prev => ({ ...prev, email: validateField('email', val) }));
    }
    if (error) { setError(''); setIsNetworkError(false); }
  };

  const handlePasswordChange = (e) => {
    const val = e?.target?.value;
    setPassword(val);
    if (touched?.password) {
      setFieldErrors(prev => ({ ...prev, password: validateField('password', val) }));
    }
    if (error) { setError(''); setIsNetworkError(false); }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const value = name === 'email' ? email : password;
    setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  if (showForgotPassword) {
    return (
      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); setForgotError(''); }}
            className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="mr-1">←</span> Back to Sign In
          </button>
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Reset Password</h2>
        <p className="text-sm text-muted-foreground mb-6">Enter your email address and we'll send you a link to reset your password.</p>

        {forgotSuccess ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800">Reset email sent!</p>
              <p className="text-sm text-green-700 mt-1">Check your inbox at <strong>{forgotEmail}</strong> for a password reset link.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {forgotError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{forgotError}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e?.target?.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  autoFocus
                />
              </div>
            </div>
            <Button type="submit" variant="default" fullWidth loading={forgotLoading}>
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {/* Error Alert */}
        {error && (
          <div className={`p-4 rounded-lg border flex items-start space-x-3 ${
            isNetworkError ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
          }`}>
            {isNetworkError ? (
              <WifiOff className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${isNetworkError ? 'text-orange-800' : 'text-red-800'}`}>
                {isNetworkError ? 'Connection Issue' : 'Sign In Failed'}
              </p>
              <p className={`text-sm mt-0.5 ${isNetworkError ? 'text-orange-700' : 'text-red-700'}`}>{error}</p>
              {retryingIn > 0 && (
                <div className="mt-2 flex items-center space-x-2">
                  <div className="flex-1 bg-orange-200 rounded-full h-1.5">
                    <div
                      className="bg-orange-500 h-1.5 rounded-full transition-all duration-1000"
                      style={{ width: `${((retryCount * 2 - retryingIn) / (retryCount * 2)) * 100}%` }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelRetry}
                    className="text-xs text-orange-600 hover:text-orange-800 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => handleBlur('email')}
              placeholder="Enter your email"
              autoComplete="email"
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors ${
                fieldErrors?.email && touched?.email ? 'border-red-400 bg-red-50' : 'border-border'
              }`}
            />
          </div>
          {fieldErrors?.email && touched?.email && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />{fieldErrors?.email}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => handleBlur('password')}
              placeholder="Enter your password"
              autoComplete="current-password"
              className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors ${
                fieldErrors?.password && touched?.password ? 'border-red-400 bg-red-50' : 'border-border'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {fieldErrors?.password && touched?.password && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />{fieldErrors?.password}
            </p>
          )}
        </div>

        {/* Forgot Password Link */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Forgot your password?
          </button>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isLoading}
          disabled={retryingIn > 0}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader className="w-4 h-4 animate-spin" />
              {retryingIn > 0 ? `Retrying in ${retryingIn}s...` : 'Signing in...'}
            </span>
          ) : 'Sign In'}
        </Button>

        {/* Demo Credentials */}
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Demo Accounts</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => fillDemoCredentials('demo@leadgenproai.us', 'Demo123!')}
              className="text-xs px-3 py-2 bg-background border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
            >
              <p className="font-medium text-foreground">Demo User</p>
              <p className="text-muted-foreground">demo@leadgenproai.us</p>
            </button>
            <button
              type="button"
              onClick={() => fillDemoCredentials('admin@leadgenproai.us', 'Admin123!')}
              className="text-xs px-3 py-2 bg-background border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
            >
              <p className="font-medium text-foreground">Admin User</p>
              <p className="text-muted-foreground">admin@leadgenproai.us</p>
            </button>
          </div>
        </div>

        {/* Register Link */}
        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:text-primary/80 font-medium transition-colors">
            Create one for free
          </Link>
        </p>
      </form>
    </div>
  );
};

export default LoginForm;